import * as React from 'react';
import { QueryString } from '@grandvisionhq/utils';
import { normaliseQueryParams } from './utils/normalise-params.js';

const useClientSideRouter = ({ basePath , initialRoute ='' , initialQueryParams ={} , onRouteChange , routerId  })=>{
    const [currentQueryParams1, setCurrentQueryParams] = React.useState(normaliseQueryParams(initialQueryParams));
    const [currentRoute, setCurrentRoute] = React.useState(initialRoute);
    const onRedirect = React.useCallback((event)=>{
        const { state  } = event;
        const { as =''  } = state !== null && state !== void 0 ? state : {};
        const [pathname, queryParams] = as.split('?');
        const routePath = pathname.replace(basePath, '');
        const [_, route] = routePath.split('/');
        var ref;
        setCurrentQueryParams(normaliseQueryParams((ref = QueryString.parse(queryParams !== null && queryParams !== void 0 ? queryParams : '')) !== null && ref !== void 0 ? ref : {}));
        setCurrentRoute(route || '');
        onRouteChange && onRouteChange(route || '');
    }, [
        basePath,
        onRouteChange
    ]);
    const onPopState = React.useCallback((event)=>{
        var ref;
        if ((event === null || event === void 0 ? void 0 : (ref = event.state) === null || ref === void 0 ? void 0 : ref.as) === undefined) {
            /*  if as is undefined, onRedirect can only assume what route we are on
            moreover there is a chance Next.js will fire getInitialProps again
            which will cause our page to crash
            for these reasons we decide to just do a full page reload

          cases where as is undefined:
            - on initial page load, browser calls replace/push/State with as undefined
            - we forgot to pass an as property to our replace/push/State
            - unknown cases such as replace/push/State fired by next.js or by the browser

          I also noticed that: assume we do around 12 navigation steps in AAB
            around 1 out of 4 will not register the "as" route. With this approach there
            will be a reload but only in these 3 cases, for all other intermediate steps
            browser still remembers the as route even though we had a reload in between.

          another note is that full redirects seem to happen mostly when the forward button
          is pressed, which is not a common use case for our users
          */ // SSR redirect
            location.reload();
        } else {
            // Client Side redirect
            onRedirect(event);
        }
    }, [
        onRedirect
    ]);
    const goTo = React.useCallback((redirectPath)=>{
        // Need to add state to pushState to not break browser back button event
        window.history.pushState({
            as: redirectPath
        }, '', redirectPath);
        onRedirect({
            state: {
                as: redirectPath
            }
        });
    }, [
        onRedirect
    ]);
    const updateCurrentQueryParams = React.useCallback((href)=>{
        var ref;
        const search = (ref = (href !== null && href !== void 0 ? href : window.location.href).split('?')[1]) !== null && ref !== void 0 ? ref : '';
        const currentQueryParams = normaliseQueryParams(QueryString.parse(search));
        setCurrentQueryParams(currentQueryParams);
    }, []);
    /*
   * Since we have no way to detect replaceState calls,
   * but we need to know when the query parameters are updated,
   * we want to hook into the replaceState calls by using a proxy.
   */ React.useEffect(()=>{
        // Store original value
        const original = window.history.replaceState;
        // Create revocable proxy
        const revocable = Proxy.revocable(window.history.replaceState, {
            apply: (target, thisArgument, args)=>{
                const result = Reflect.apply(target, thisArgument, args) // Call original implementation
                ;
                const url = args[2];
                updateCurrentQueryParams(url instanceof URL ? url.href : url) // Update query params we use across the app
                ;
                return result;
            }
        });
        // Temporarily replace original implementation by the proxy. Is reverted on unmounting.
        window.history.replaceState = revocable.proxy;
        return ()=>{
            window.history.replaceState = original // Restore original implementation
            ;
            revocable.revoke() // Revoke proxy
            ;
        };
    }, [
        updateCurrentQueryParams
    ]);
    React.useEffect(()=>{
        window.addEventListener('popstate', onPopState);
        return ()=>{
            window.removeEventListener('popstate', onPopState);
        };
    }, [
        onRedirect
    ]);
    return React.useMemo(()=>[
            {
                goTo
            },
            {
                basePath,
                currentRoute,
                queryParams: currentQueryParams1,
                routerId
            }
        ]
    , [
        goTo,
        basePath,
        currentRoute,
        currentQueryParams1,
        routerId
    ]);
};

export { useClientSideRouter as default };
//# sourceMappingURL=use-client-side-router.js.map
